import React from 'react';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon, FileTextIcon } from 'lucide-react';
import { showInConciseMode } from '../chat/threadSettings';
import { cn } from '@/lib/utils';
import { Card } from '../ui/card';

interface AppendToEllieArtifactToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

// Shown for each part Ellie adds to a long artifact after the user approved building it in chunks.
const AppendToEllieArtifactToolCallComponent: React.FC<AppendToEllieArtifactToolCallProps> = ({
  result,
  status,
}) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  let isSuccess = false;
  let errorMessage = '';
  if (isComplete && result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      isSuccess = parsed?.success === true;
      if (!isSuccess) {
        errorMessage = parsed?.error || 'Failed to add to artifact';
      }
    } catch {
      isSuccess = false;
    }
  }

  const hasError = isError || (isComplete && !isSuccess);

  return (
    <Card>
      <div className={cn(
        'p-3',
        isRunning
          ? 'bg-primary/10 border-b border-primary/20'
          : 'bg-muted/50 border-b border-border'
      )}>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            {isRunning ? (
              <div className="flex items-center gap-2">
                <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">Adding to artifact...</p>
              </div>
            ) : hasError ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <FileTextIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">Add to Artifact</p>
                </div>
                <div className="flex items-center gap-2">
                  <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                  <p className="text-sm m-0 text-destructive">{errorMessage || 'Failed to add to artifact'}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <FileTextIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">Add to Artifact</p>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                  <p className="text-sm m-0 text-foreground">Added the next part to the artifact</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export const AppendToEllieArtifactToolCall = AppendToEllieArtifactToolCallComponent as React.ComponentType<any>;

// Part of building a user-facing artifact — keep visible in concise mode
showInConciseMode(AppendToEllieArtifactToolCall);
